import {
  type CreatureKind,
  type CreatureState,
  type StatusEffect,
  type StudyLevel,
  type RoomState,
} from "./schema/fabula_ultima/RoomState";

function isLegalDiceSize(size: number): boolean {
  return size === 6 || size === 8 || size === 10 || size === 12;
}

// Fabula Ultima官方定义的六种状态效果
function isLegalStatusEffect(effect: string): effect is StatusEffect {
  return ["slow", "dazed", "weak", "shaken", "enraged", "poisoned"].includes(effect);
}

function isLegalStudyLevel(level: string): level is StudyLevel {
  return ["null", "7+", "10+", "13+"].includes(level);
}

function isDefFixedBuff(buff: string): boolean {
  return buff.startsWith("defFixed");
}

function getDefFixedBuffValue(buff: string): number | null {
  if (!isDefFixedBuff(buff)) {
    return null
  }
  return parseInt(buff.replace(/^\D+/, ""), 10);
}

function isMDefFixedBuff(buff: string): boolean {
  return buff.startsWith("mDefFixed");
}

/** Returns whether a living creature has reached its crisis HP threshold. */
function isInHpCrisis(currentHp: number, maxHp: number): boolean {
  return currentHp > 0 && currentHp * 2 <= maxHp;
}

/** Returns whether a creature has been reduced to 0 HP. Mutually exclusive with crisis. */
function isDefeated(currentHp: number): boolean {
  return currentHp <= 0;
}

function getMDefFixedBuffValue(buff: string): number | null {
  if (!isMDefFixedBuff(buff)) {
    return null
  }
  return parseInt(buff.replace(/^\D+/, ""), 10);
}

interface ICreature {
  name: string;
  /** Browser-local source identity, used to cancel work after a state replacement. */
  readonly identity?: object;
  avatar?: string;
  kind: CreatureKind;
  hp: number;
  maxHp: number;
  mp: number;
  maxMp: number;
  ip: number;
  maxIp: number;
  dex: number;
  ins: number;
  mig: number;
  wlp: number;
  currentDex: number;
  currentIns: number;
  currentMig: number;
  currentWlp: number;
  physicalDefense: number;
  magicDefense: number;
  fixedPhysicalDefense: boolean;
  statusEffects: ReadonlyArray<StatusEffect>;
  buffs: ReadonlyArray<string>;
  studyLevel: StudyLevel;
}

class CreatureObject implements ICreature {
  constructor(
    private readonly creature: Readonly<CreatureState>
  ) {}

  get name(): string {
    return this.creature.name;
  }

  get avatar(): string {
    return this.creature.avatar;
  }

  get identity(): object {
    return this.creature;
  }

  get kind(): CreatureKind {
    return this.creature.kind;
  }

  get hp(): number {
    return this.creature.hp;
  }

  get maxHp(): number {
    return this.creature.maxHp;
  }

  get mp(): number {
    return this.creature.mp;
  }

  get maxMp(): number {
    return this.creature.maxMp;
  }

  get ip(): number {
    return this.creature.ip;
  }

  get maxIp(): number {
    return this.creature.maxIp;
  }

  get dex(): number {
    return this.creature.dex;
  }

  get ins(): number {
    return this.creature.ins;
  }

  get mig(): number {
    return this.creature.mig;
  }

  get wlp(): number {
    return this.creature.wlp;
  }

  get statusEffects(): ReadonlyArray<StatusEffect> {
    return Array.from(this.creature.statusEffects.values());
  }

  get buffs(): ReadonlyArray<string> {
    return Array.from(this.creature.buffs.values());
  }

  get physicalDefenseModifier(): number {
    return this.creature.physicalDefenseModifier;
  }

  get magicDefenseModifier(): number {
    return this.creature.magicDefenseModifier;
  }

  get fixedPhysicalDefense(): boolean {
    return this.creature.fixedPhysicalDefense;
  }

  get currentDex(): number {
    let currentDex = this.dex;
    if (this.statusEffects.includes("slow")) {
      currentDex -= 2;
    }
    if (this.statusEffects.includes("enraged")) {
      currentDex -= 2;
    }
    return Math.max(6, currentDex);
  }

  get currentIns(): number {
    let currentIns = this.ins;
    if (this.statusEffects.includes("dazed")) {
      currentIns -= 2;
    }
    if (this.statusEffects.includes("enraged")) {
      currentIns -= 2;
    }
    return Math.max(6, currentIns);
  }

  get currentMig(): number {
    let currentMig = this.mig;
    if (this.statusEffects.includes("weak")) {
      currentMig -= 2;
    }
    if (this.statusEffects.includes("poisoned")) {
      currentMig -= 2;
    }
    return Math.max(6, currentMig);
  }

  get currentWlp(): number {
    let currentWlp = this.wlp;
    if (this.statusEffects.includes("shaken")) {
      currentWlp -= 2;
    }
    if (this.statusEffects.includes("poisoned")) {
      currentWlp -= 2;
    }
    return Math.max(6, currentWlp);
  }

  get physicalDefense(): number {
    for (const buff of this.creature.buffs) {
      if (isDefFixedBuff(buff)) {
        return getDefFixedBuffValue(buff)!;
      }
    }
    if (this.fixedPhysicalDefense) {
      return this.physicalDefenseModifier;
    }
    return this.physicalDefenseModifier + this.currentDex;
  }

  get magicDefense(): number {
    for (const buff of this.creature.buffs) {
      if (isMDefFixedBuff(buff)) {
        return getMDefFixedBuffValue(buff)!;
      }
    }
    return this.magicDefenseModifier + this.currentIns;
  }

  get studyLevel(): StudyLevel {
    return this.creature.studyLevel;
  }
}

const kRoomName = "fu_room";

namespace Api {
  export type RoomJoinOptions = {
    user: string,
    isHost: boolean,
  }

  // Direction: Client -> Server
  export const kRunCommandsName = "runCommands";
  export type RunCommandsRequest = {
    // Each element is a command to be run by the server, separated by spaces. Example:
    // "hp A 10"
    commands: string[];
  }

  // Direction: Client -> Server. Only the room host may move the timeline.
  export const kMoveStateTimelineName = "moveStateTimeline";
  export type MoveStateTimelineRequest = {
    // The number of timeline entries to apply, in the inclusive range [0, length].
    position: number;
  };

  // Direction: Client -> Server. Permanently discards future history at position.
  export const kRollbackStateTimelineName = "rollbackStateTimeline";
  export type RollbackStateTimelineRequest = { position: number; };

  // Direction: Client -> Server
  export const kDetectIntentName = "detectIntent";
  export type DetectIntentRequest = {
    query: string;
    requestId: string;
  }
  export const kDetectIntentResponseName = kDetectIntentName + "Response";
  export type DetectIntentResponse = {
    requestId: string;
    response: string;
    error?: string;
  }

  // Direction: Client -> Server
  export const kHeartbeatName = "heartbeat";
  export type HeartbeatRequest = {};

  // Direction: Client -> Server
  export const kUploadGameStateName = "uploadGameState";
  // @see CreatureState in schema/fabula_ultima/RoomState.ts — stores the same persistent fields.
  // @see ICreature in this file — adds computed properties (currentDex, physicalDefense, etc.).
  // When adding a field to CreatureState, also add it here and update serializeCreature() in GameStateButton.tsx.
  export type UploadGameStateCreature = {
    name: string;
    avatar?: string;
    kind: CreatureKind;
    hp: number;
    maxHp: number;
    mp: number;
    maxMp: number;
    ip: number;
    maxIp: number;
    dex: number;
    ins: number;
    mig: number;
    wlp: number;
    physicalDefenseModifier: number;
    magicDefenseModifier: number;
    fixedPhysicalDefense: boolean;
    studyLevel: StudyLevel;
    statusEffects: StatusEffect[];
    buffs: string[];
  };
  export type UploadGameStateCommandHistory = {
    commandString: string;
    isSensitive: boolean;
    redactedCommandString: string;
    timestamp: number;
  };
  export const kUploadGameStateVersion = 1;
  export type UploadGameStateRequest = {
    version: number;
    creatures: UploadGameStateCreature[];
    commandHistory: UploadGameStateCommandHistory[];
  };
  // The save file format is the same as the upload request.
  export type GameStateSaveData = UploadGameStateRequest;

  // Direction: Server -> Client
  // export const kProceedToNextRoundEvent = "proceedToNextRoundEvent";
  export const kBroadcastErrorName = "broadcastError";
  export type BroadcastErrorMessage = {
    error: string;
  }
};

export {
  type CreatureKind,
  type ICreature,
  type RoomState,
  type StatusEffect,
  type StudyLevel,
  Api,
  CreatureObject,
  kRoomName,
  isLegalDiceSize,
  isLegalStatusEffect,
  isLegalStudyLevel,
  isDefFixedBuff,
  isMDefFixedBuff,
  isInHpCrisis,
  isDefeated,
  getDefFixedBuffValue,
  getMDefFixedBuffValue,
};
